import { defineStore } from 'pinia'
import {
  fetchPortalConfigByHost,
  normalizePortalConfig,
  type PortalConfig,
} from '@/services/portalConfigApi'
import {
  applyPortalBranding,
  cachePortalBranding,
  clearCachedPortalBranding,
} from '@/utils/portalBranding'
import { resolvePortalConfigHost, shouldFetchPortalConfig } from '@/utils/portalHost'
import type { Contractor } from '@/services/contractorService'

export const usePortalConfigStore = defineStore('portalConfig', {
  state: () => ({
    config: null as PortalConfig | null,
    hostname: '',
    loaded: false,
    loading: false,
  }),

  getters: {
    companyName: (state) => state.config?.company_name || 'Client Portal',
    logoUrl: (state) => state.config?.logo_url || '',
    loginHint: (state) => state.config?.login_hint || 'Log in to view your fleet profile and data.',
    primaryColor: (state) => state.config?.primary_color || '',
  },

  actions: {
    setConfig(config: PortalConfig | null, hostname?: string) {
      this.config = config
      if (hostname) this.hostname = hostname.toLowerCase()
      applyPortalBranding(config)
      if (config && this.hostname) cachePortalBranding(config, this.hostname)
    },

    mergeFromContractor(contractor: Contractor | null | undefined) {
      if (!contractor || !shouldFetchPortalConfig()) return
      const merged = normalizePortalConfig(contractor)
      if (!merged) return
      this.config = { ...(this.config || {}), ...merged }
      applyPortalBranding(this.config)
    },

    resetForGenericHost() {
      this.config = null
      this.loaded = true
      this.loading = false
      clearCachedPortalBranding()
      applyPortalBranding(null)
    },

    async loadForCurrentHost(force = false): Promise<PortalConfig | null> {
      if (typeof window === 'undefined') return null
      if (this.loaded && !force) return this.config

      const hostname = window.location.hostname.toLowerCase()
      this.hostname = hostname
      this.loading = true

      try {
        if (!shouldFetchPortalConfig(hostname)) {
          this.resetForGenericHost()
          return null
        }

        const lookupHost = resolvePortalConfigHost(hostname)!

        const fromApi = await fetchPortalConfigByHost(lookupHost)
        if (fromApi) {
          this.setConfig(fromApi, lookupHost)
        } else {
          clearCachedPortalBranding()
          this.setConfig(null, lookupHost)
        }

        this.loaded = true
        return this.config
      } finally {
        this.loading = false
      }
    },
  },
})
